import { useId } from "react"
import type { MagentoAttempt, MagentoAttemptDecision, MagentoAttemptPreview } from "@nova-ai/shared"
import { useMagentoAttemptSelection } from "../../services/use-magento-attempt-selection"
import type { OrderDetail } from "../../services/use-orders"
import { Button } from "../ui/button"
import { formatDateTime } from "../../lib/format-date"

export function MagentoAttempts({ data }: { data: OrderDetail }) {
  const groupName = useId()
  const selection = useMagentoAttemptSelection(data.marketplace, data.orderId)
  const reviewedPreview = selection.reviewedSet ? selection.preview.data : undefined
  const attempts = reviewedPreview?.candidates ?? data.magento.attemptHistory
  const error = selection.selection.error ?? selection.preview.error
  return (
    <section aria-label="Magento attempts" className="space-y-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Choose from fresh exact matches. Status, money and warehouse data follow your choice.
        </p>

        <Button
          variant="secondary"
          className="min-h-11 w-full sm:w-auto"
          onClick={() => void selection.refresh()}
          disabled={selection.preview.isFetching || selection.selection.isPending}
        >
          {selection.preview.isFetching ? "Refreshing…" : "Check Magento for fresh matches"}
        </Button>
      </div>

      {data.magento.state === "ambiguous" ? (
        <p className="text-xs text-warning">
          Magento evidence is ambiguous. Review fresh candidates and select the confirmed attempt.
        </p>
      ) : null}

      <AttemptOptions
        data={data}
        groupName={groupName}
        preview={reviewedPreview}
        attempts={attempts}
        entityId={selection.entityId}
        setEntityId={selection.setEntityId}
        pending={selection.selection.isPending}
      />

      {reviewedPreview ? (
        <div className="space-y-3 border-t border-border pt-3">
          <label className="block text-xs font-medium">
            Why this one? (required)
            <textarea
              aria-label="Selection reason"
              value={selection.reason}
              onChange={(event) => selection.setReason(event.target.value)}
              maxLength={2000}
              rows={2}
              disabled={selection.selection.isPending}
              className="mt-1 block w-full rounded-lg border border-border bg-card p-2 text-sm focus-visible:outline-2 focus-visible:outline-ring"
            />
          </label>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              className="min-h-11"
              disabled={selection.selection.isPending}
              onClick={selection.cancel}
            >
              Cancel
            </Button>

            <Button
              className="min-h-11"
              variant="primary"
              disabled={!selection.canSubmit}
              onClick={() => void selection.submit()}
            >
              {selection.selection.isPending ? "Saving…" : "Save Magento selection"}
            </Button>
          </div>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error.message} Review fresh candidates before retrying.
        </p>
      ) : null}

      {selection.selection.isSuccess ? (
        <p role="status" className="text-xs text-success">
          Selection saved. Warehouse data will reload on the next refresh.
        </p>
      ) : null}

      <SelectionHistory decisions={data.magentoDecisions} />
    </section>
  )
}

function AttemptOptions({
  data,
  groupName,
  preview,
  attempts,
  entityId,
  setEntityId,
  pending,
}: {
  data: OrderDetail
  groupName: string
  preview: MagentoAttemptPreview | undefined
  attempts: MagentoAttempt[]
  entityId: string
  setEntityId: (value: string) => void
  pending: boolean
}) {
  return (
    <div
      role={preview ? "radiogroup" : undefined}
      aria-label="Magento attempts to follow"
      className="divide-y divide-border"
    >
      {attempts.map((attempt) => (
        <label key={attempt.entityId} className="flex min-h-11 items-start gap-3 py-3 text-sm">
          {preview ? (
            <input
              type="radio"
              name={groupName}
              value={attempt.entityId}
              checked={entityId === attempt.entityId}
              disabled={pending || !preview.selectableEntityIds.includes(attempt.entityId)}
              onChange={() => setEntityId(attempt.entityId)}
              className="mt-1 size-4 shrink-0 accent-primary"
            />
          ) : null}

          <span className="min-w-0 flex-1">
            <span className="font-mono">{attempt.incrementId}</span>

            {data.magento.entityId === attempt.entityId ? (
              <span className="ml-2 text-xs text-info">Current</span>
            ) : null}

            <span className="mt-1 block text-xs text-muted-foreground">{`${attempt.status} · entity ${attempt.entityId} · created ${formatDateTime(attempt.createdAt)}`}</span>
          </span>

          <span className="shrink-0 text-xs tabular-nums">{`${attempt.grandTotal.toFixed(2)} ${data.currency}`}</span>
        </label>
      ))}

      {attempts.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">
          No attempt history recorded yet. Check Magento for fresh matches.
        </p>
      ) : null}

      {preview && data.magento.manualEntityId ? (
        <label className="flex min-h-11 items-center gap-3 text-sm">
          <input
            type="radio"
            name={groupName}
            value="clear"
            checked={entityId === "clear"}
            onChange={() => setEntityId("clear")}
            disabled={pending}
          />
          Clear manual selection (duplicates become ambiguous)
        </label>
      ) : null}
    </div>
  )
}

function SelectionHistory({ decisions }: { decisions: MagentoAttemptDecision[] }) {
  if (decisions.length === 0) {
    return null
  }
  return (
    <details>
      <summary className="min-h-11 cursor-pointer content-center text-xs font-medium">{`Selection history (${decisions.length})`}</summary>

      <ul className="mt-2 space-y-2 text-xs">
        {decisions.map((decision) => (
          <li key={decision.version}>
            <p>{decisionByline(decision)}</p>

            <p className="mt-1 text-muted-foreground">{decision.reason}</p>
          </li>
        ))}
      </ul>
    </details>
  )
}

function decisionByline(decision: MagentoAttemptDecision): string {
  const action = decision.entityId
    ? `Selected entity ${decision.entityId}`
    : "Cleared manual selection"
  return `${action} · ${decision.actor} · ${formatDateTime(decision.selectedAt)}`
}
